import React from "react";
import { Box, Tab, Tabs } from "@mui/material";

const BLUE = "#3451F4";
const PURPLE = "#9C4FFF";
const BORDER = "#E5E7EB";
const COUNT_BG = "#F2E9FF";
const ACTIVE_COUNT_BG = "#EAF2FF";
const TAB_FONT_FAMILY = "Be Vietnam Pro";

function normalizeTabLabelText(label) {
  if (typeof label !== "string") return label;

  const trimmedLabel = label.trim();
  const hasLetters = /[A-Za-z]/.test(trimmedLabel);
  const isAllUppercase =
    hasLetters &&
    trimmedLabel === trimmedLabel.toUpperCase() &&
    trimmedLabel !== trimmedLabel.toLowerCase();

  if (!isAllUppercase) return label;

  return trimmedLabel
    .split(/(\s+)/)
    .map((part) =>
      /\s+/.test(part) || !part
        ? part
        : `${part.charAt(0)}${part.slice(1).toLowerCase()}`
    )
    .join("");
}

export function TabNavigationLabel({
  label,
  count,
  active = false,
  labelSx,
  countSx,
  contentSizedIndicator = false,
  contentSizedIndicatorOffset = -8,
  contentSizedIndicatorHeight = 3,
}) {
  const showCount = count !== undefined && count !== null && count !== "";

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 1,
        width: "100%",
        textAlign: "left",
        lineHeight: "24px",
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1,
          width: "auto",
          flex: "0 0 auto",
          position: "relative",
            ...(active && contentSizedIndicator
            ? {
                "&::after": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: contentSizedIndicatorOffset,
                  height: contentSizedIndicatorHeight,
                  borderRadius: 999,
                  backgroundColor: BLUE,
                },
              }
            : {}),
        }}
      >
      <Box
        component="span"
        sx={{
          color: PURPLE,
          fontFamily: TAB_FONT_FAMILY,
          fontStyle: "normal",
          fontSize: 14,
          fontWeight: 400,
          lineHeight: "24px",
          textTransform: "capitalize !important",
          letterSpacing: 0,
          whiteSpace: "nowrap",
          ...labelSx,
        }}
      >
        {normalizeTabLabelText(label)}
      </Box>

      {showCount ? (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 18,
            px: 0.75,
            height: 18,
            borderRadius: 999,
            backgroundColor: active ? ACTIVE_COUNT_BG : COUNT_BG,
            color: PURPLE,
            fontSize: 14,
            fontWeight: 500,
            lineHeight: 1,
            fontFamily: TAB_FONT_FAMILY,
            textTransform: "capitalize !important",
            letterSpacing: 0,
            ...countSx,
          }}
        >
          {count}
        </Box>
      ) : null}
      </Box>
    </Box>
  );
}

export default function TabNavigationBar({
  value,
  onChange,
  items = [],
  wrapperClassName,
  tabsClassName,
  wrapperSx,
  tabsSx,
  labelSx,
  countSx,
  borderBottom = true,
  dividerColor = BORDER,
  activeColor = PURPLE,
  idleColor = PURPLE,
  indicatorColor = BLUE,
  indicatorHeight = 3,
  indicatorBorderRadius = 0,
  minHeight = 36,
  tabGap = 3,
  variant = "scrollable",
  scrollButtons = false,
  allowScrollButtonsMobile = false,
  contentSizedIndicator = false,
  contentSizedIndicatorOffset = -8,
  contentSizedIndicatorHeight = 3,
}) {
  const safeItems = Array.isArray(items) ? items : [];

  return (
    <Box
      className={wrapperClassName}
      sx={{
        minWidth: 0,
        mb: "-2px",
        ...(borderBottom ? { borderBottom: `1px solid ${dividerColor}` } : {}),
        ...wrapperSx,
      }}
    >
      <Tabs
        className={tabsClassName}
        value={value}
        onChange={(event, nextValue) => {
          onChange?.(nextValue, event);
        }}
        variant={variant}
        scrollButtons={scrollButtons}
        allowScrollButtonsMobile={allowScrollButtonsMobile}
        sx={{
          minHeight,
          maxWidth: "100%",
          overflow: "visible",
          "& .MuiTabs-flexContainer": {
            gap: tabGap,
          },
          "& .MuiTabs-indicator": {
            backgroundColor: contentSizedIndicator ? "transparent" : indicatorColor,
            height: indicatorHeight,
            bottom: "2px",
            zIndex: 1,
            borderRadius: indicatorBorderRadius,
            transition:
              "left 280ms cubic-bezier(0.4, 0, 0.2, 1), width 280ms cubic-bezier(0.4, 0, 0.2, 1), transform 280ms cubic-bezier(0.4, 0, 0.2, 1)",
          },
          "& .MuiTab-root": {
            minHeight,
            textTransform: "capitalize !important",
            px: 0,
            minWidth: "auto",
            color: idleColor,
            opacity: 1,
            alignItems: "flex-start",
            justifyContent: "flex-start",
            textAlign: "left",
            fontFamily: TAB_FONT_FAMILY,
           
            fontStyle: "normal",
            fontSize: 14,
            fontWeight: 400,
            lineHeight: "24px",
          },
          "& .MuiTab-root.Mui-selected": {
            color: `${activeColor} !important`,
          },
          ...tabsSx,
        }}
      >
        {safeItems.map((item) => (
          <Tab
            key={item.value}
            value={item.value}
            disableRipple
            disabled={Boolean(item.disabled)}
            label={
              <TabNavigationLabel
                active={item.value === value}
                label={item.label}
                count={item.count}
                labelSx={labelSx}
                countSx={countSx}
                contentSizedIndicator={contentSizedIndicator}
                contentSizedIndicatorOffset={contentSizedIndicatorOffset}
                contentSizedIndicatorHeight={contentSizedIndicatorHeight}
              />
            }
          />
        ))}
      </Tabs>
    </Box>
  );
}
